// Typed callable mutations. All writes go through these — never direct
// Firestore writes (rules close them). Error branching happens on
// CallableError.serverCode (see callable.ts).
import { call } from './callable'
import type { WeekEvent } from '../lib/types'

export interface ShiftClockPayload {
  date: string
  startTime: string
  endTime: string
  endsNextDay?: boolean
  breakMinutes?: number
}

export interface UpsertFlags {
  force?: boolean
  overrideCredentials?: boolean
  confirmOvertime?: boolean
  supersedePending?: boolean
}

export const api = {
  // identity / roster
  provisionSelf: () => call<{ status: string }>('provisionSelf', {}),
  setupEmployee: (input: {
    email: string
    departmentId: string
    homeTeamId: string
    positionIds: string[]
    role: string
    supervisorTeamIds?: string[]
  }) => call<{ ok: true }>('setupEmployee', input),
  updateEmployee: (input: {
    email: string
    homeTeamId?: string
    positionIds?: string[]
    role?: string
    supervisorTeamIds?: string[]
    otThresholdHours?: number | null
    status?: 'active' | 'inactive'
  }) => call<{ ok: true }>('updateEmployee', input),
  syncRosterNow: () => call<{ total: number; added: string[]; removed: string[] }>('syncRosterNow', {}),

  // org structure
  manageDepartment: (input: Record<string, unknown>) => call<{ ok: true }>('manageDepartment', input),
  manageTeam: (input: Record<string, unknown>) => call<{ ok: true; id?: string }>('manageTeam', input),
  managePosition: (input: Record<string, unknown>) => call<{ ok: true; id?: string }>('managePosition', input),
  manageCredentialType: (input: Record<string, unknown>) =>
    call<{ ok: true; id?: string }>('manageCredentialType', input),
  manageCredential: (input: Record<string, unknown>) => call<{ ok: true; id?: string }>('manageCredential', input),

  // scheduling
  createShift: (
    input: {
      departmentId: string
      teamId: string
      positionId: string
      assigneeEmail?: string | null
      notes?: string | null
    } & ShiftClockPayload &
      UpsertFlags,
  ) => call<{ status: string; shiftId?: string; otRequestId?: string }>('createShift', input),
  updateShift: (
    input: {
      shiftId: string
      teamId?: string
      positionId?: string
      notes?: string | null
      expectedVersion?: number
    } & Partial<ShiftClockPayload> &
      UpsertFlags,
  ) => call<{ status: string; otRequestId?: string }>('updateShift', input),
  assignShift: (
    input: { shiftId: string; assigneeEmail: string | null; expectedVersion?: number } & UpsertFlags,
  ) => call<{ status: string; otRequestId?: string }>('assignShift', input),
  cancelShift: (input: { shiftId: string }) => call<{ status: string }>('cancelShift', input),
  publishWeek: (input: { departmentId: string; weekStart: string; overtimeConfirmations?: string[] }) =>
    call<{
      status: 'published' | 'needs_overtime'
      shiftCount?: number
      notified?: number
      needsOvertime?: { email: string; name: string; hours: number; thresholdHours: number }[]
    }>('publishWeek', input),
  copyWeek: (input: { departmentId: string; sourceWeekStart: string; targetWeekStart: string; merge?: boolean }) =>
    call<{ status: string; created: number; opened: number }>('copyWeek', input),
  saveTemplate: (input: {
    departmentId: string
    name: string
    description?: string | null
    weekStart: string
    keepAssignees?: boolean
  }) => call<{ status: string; id: string }>('saveTemplate', input),
  applyTemplate: (input: { departmentId: string; templateId: string; weekStart: string; merge?: boolean }) =>
    call<{ status: string; created: number }>('applyTemplate', input),
  updateWeekEvents: (input: { departmentId: string; weekStart: string; events: WeekEvent[] }) =>
    call<{ status: string }>('updateWeekEvents', input),
  logExport: (input: { departmentId: string; scope: 'week' | 'day'; key: string; format: 'pdf' | 'csv' }) =>
    call<{ status: string }>('logExport', input),

  // requests
  requestSwap: (input: { myShiftId: string; otherShiftId: string }) =>
    call<{ status: string; requestId: string }>('requestSwap', input),
  respondSwap: (input: { requestId: string; accept: boolean }) => call<{ status: string }>('respondSwap', input),
  requestDrop: (input: { shiftId: string; reason: string }) => call<{ status: string }>('requestDrop', input),
  requestClaim: (input: { shiftId: string }) => call<{ status: string }>('requestClaim', input),
  requestTimeOff: (input: {
    startDate: string
    endDate: string
    reason?: string | null
    partial?: { startTime: string; endTime: string } | null
  }) => call<{ status: string }>('requestTimeOff', input),
  cancelRequest: (input: { requestId: string }) => call<{ status: string }>('cancelRequest', input),
  decideRequest: (input: {
    requestId: string
    decision: 'approve' | 'deny'
    note?: string | null
    resolution?: { kind: 'open' } | { kind: 'reassign'; email: string }
    conflictResolutions?: Record<string, 'open' | 'keep'>
    confirmOvertime?: boolean
  }) => call<{ status: string }>('decideRequest', input),
  setAvailability: (input: {
    email?: string
    weekly: Record<string, { start: string; end: string }[]>
    notes?: string | null
  }) => call<{ status: string }>('setAvailability', input),
  acknowledgeShift: (input: { shiftId: string }) => call<{ status: string }>('acknowledgeShift', input),
}
